import React, { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ResumeItem } from '../services/apiService'
import { ResumeFitAnalysisResponse, ResumeSelection, TailoredResumeResponse } from '../services/jobApplicationService'
import { Step1FitAnalysis } from './ResumeSelectionSidebar/Step1FitAnalysis'
import { Step2ResumeSelection } from './ResumeSelectionSidebar/Step2ResumeSelection'
import { Step3CustomResume } from './ResumeSelectionSidebar/Step3CustomResume'
import { Step4Confirmation } from './ResumeSelectionSidebar/Step4Confirmation'
import './ResumeSelectionSidebar.css'

interface ResumeSelectionSidebarProps {
  isOpen: boolean
  onClose: () => void
  onBackToAssignment?: () => void // Callback to return to resume assignment modal
  jobApplicationId: string
  fitAnalysis: ResumeFitAnalysisResponse | null
  availableResumes: ResumeItem[]
  tailoredResume: TailoredResumeResponse | null
  selectedResumeId?: string | null
  resumeSelectionType?: 'saved' | 'custom' | null
  onResumeSelectionChange: (selection: ResumeSelection) => Promise<void>
  onTailoredResumeUpdated: () => void
  onSelectionSaved?: () => void
}

type WizardStep = 1 | 2 | 3 | 4

export const ResumeSelectionSidebar: React.FC<ResumeSelectionSidebarProps> = ({
  isOpen,
  onClose,
  onBackToAssignment,
  jobApplicationId,
  fitAnalysis,
  availableResumes,
  tailoredResume,
  selectedResumeId,
  resumeSelectionType,
  onResumeSelectionChange,
  onTailoredResumeUpdated,
  onSelectionSaved,
}) => {
  const [isMounted, setIsMounted] = useState(false)
  const [currentStep, setCurrentStep] = useState<WizardStep>(1)
  const [selectedResumeIdLocal, setSelectedResumeIdLocal] = useState<string | null>(selectedResumeId || null)
  const [selectionTypeLocal, setSelectionTypeLocal] = useState<'best-fit' | 'saved' | 'custom'>(
    resumeSelectionType === 'custom' ? 'custom' : 
    resumeSelectionType === 'saved' ? 'saved' : 
    'best-fit'
  )
  
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Calculate best fit resume
  const bestFitAnalysis = fitAnalysis?.resumeFitAnalyses && fitAnalysis.resumeFitAnalyses.length > 0
    ? [...fitAnalysis.resumeFitAnalyses].sort((a, b) => b.fitScore - a.fitScore)[0]
    : null
  const bestFitResumeId = bestFitAnalysis?.resumeId || null

  const handleNext = useCallback(() => {
    if (currentStep < 4) {
      // If on step 2 and custom is not selected, skip step 3
      if (currentStep === 2 && selectionTypeLocal !== 'custom' && !selectedResumeIdLocal?.startsWith('tailored-')) {
        setCurrentStep(4)
      } else {
        setCurrentStep((prev) => (prev + 1) as WizardStep)
      }
    }
  }, [currentStep, selectionTypeLocal, selectedResumeIdLocal])

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as WizardStep)
    }
  }, [currentStep])

  const handleStepChange = useCallback((step: WizardStep) => {
    setCurrentStep(step)
  }, [])

  const handleResumeSelect = useCallback((resumeId: string | null, type: 'best-fit' | 'saved' | 'custom') => {
    setSelectedResumeIdLocal(resumeId)
    setSelectionTypeLocal(type)
  }, [])

  const handleSaveAndClose = useCallback(async () => {
    try {
      // Determine the final selection
      const isTailoredResume = selectedResumeIdLocal?.startsWith('tailored-')
      const finalSelection: ResumeSelection = 
        selectionTypeLocal === 'custom' || isTailoredResume
          ? { type: 'custom' }
          : {
              type: 'saved',
              resumeId: selectionTypeLocal === 'best-fit' 
                ? bestFitResumeId || null
                : selectedResumeIdLocal || null,
            }

      await onResumeSelectionChange(finalSelection)
      onSelectionSaved?.()
      onClose()
    } catch (error) {
      // Error handling is done in Step4
      throw error
    }
  }, [selectedResumeIdLocal, selectionTypeLocal, bestFitResumeId, onResumeSelectionChange, onSelectionSaved, onClose])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  // Only render portal after component is mounted (client-side only)
  // This prevents React error #310 (hydration mismatch)
  if (!isMounted || typeof document === 'undefined' || !document.body) {
    return null
  }

  return createPortal(
    <div className="resume-selection-sidebar-backdrop" onClick={handleBackdropClick}>
      <div 
        className="resume-selection-sidebar"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="resume-selection-sidebar-header">
          <div className="resume-selection-sidebar-title">
            {onBackToAssignment && (
              <button
                className="resume-selection-sidebar-back"
                onClick={onBackToAssignment}
                title="Back to Resume Assignment"
              >
                ← Back
              </button>
            )}
            <h2>Resume Selection</h2>
            <p className="resume-selection-sidebar-subtitle">
              Choose and customize your resume for this application
            </p>
          </div>
          <button 
            className="resume-selection-sidebar-close"
            onClick={onClose}
            title="Close resume selection"
          >
            ×
          </button>
        </div>

        {/* Step Indicator */}
        <div className="resume-selection-sidebar-steps">
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className={`resume-selection-sidebar-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
              onClick={() => handleStepChange(step as WizardStep)}
            >
              <div className="resume-selection-sidebar-step-number">{step}</div>
              <div className="resume-selection-sidebar-step-label">
                {step === 1 && 'Fit Analysis'}
                {step === 2 && 'Select Resume'}
                {step === 3 && 'Customize'}
                {step === 4 && 'Confirm'}
              </div>
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="resume-selection-sidebar-content">
          {currentStep === 1 && (
            <Step1FitAnalysis
              fitAnalysis={fitAnalysis}
              availableResumes={availableResumes}
              onNext={handleNext}
            />
          )}
          {currentStep === 2 && (
            <Step2ResumeSelection
              fitAnalysis={fitAnalysis}
              availableResumes={availableResumes}
              tailoredResume={tailoredResume}
              selectedResumeId={selectedResumeIdLocal}
              selectionType={selectionTypeLocal}
              bestFitResumeId={bestFitResumeId}
              onResumeSelect={handleResumeSelect}
              onNext={() => {
                // If custom is selected, go to step 3, otherwise skip to step 4
                if (selectionTypeLocal === 'custom' || selectedResumeIdLocal?.startsWith('tailored-')) {
                  setCurrentStep(3)
                } else {
                  setCurrentStep(4)
                }
              }}
              onBack={handleBack}
            />
          )}
          {currentStep === 3 && (
            <Step3CustomResume
              jobApplicationId={jobApplicationId}
              tailoredResume={tailoredResume}
              selectedResumeId={selectedResumeIdLocal}
              selectionType={selectionTypeLocal}
              availableResumes={availableResumes}
              onTailoredResumeUpdated={onTailoredResumeUpdated}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}
          {currentStep === 4 && (
            <Step4Confirmation
              fitAnalysis={fitAnalysis}
              availableResumes={availableResumes}
              tailoredResume={tailoredResume}
              selectedResumeId={selectedResumeIdLocal}
              selectionType={selectionTypeLocal}
              bestFitResumeId={bestFitResumeId}
              onSave={handleSaveAndClose}
              onBack={handleBack}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

